// src/features/map/labelFields.js
// Label field catalog + value/colour evaluation for map label badges (spec §4).
// Pure: callers pass `t`, the viewer's speed unit and the fuel overview row in `ctx`.
import { CAN_SIGNALS, resolveCanSignals, parseSensorConfig } from '@trackizy/formatters';
import { resolveFuelLevel } from '@/features/fuel/fuelLevelResolver';
import { isPositionStale } from '@/lib/positionFreshness';
import { isStationaryFix, STATUS_CONFIG } from '@/lib/utils';
import { knotsToUnit } from '@/hooks/useUnits';
import { formatSensorValue } from '@/features/replay/replayMarkerLabel';
import { canLabel } from './canCards';
import { TEXT_SIZE_PX } from './labelStyle';
import { cardSpec, registerCardSpec } from './labelCards';

const DASH = '—';
const SPEED_LABELS = { kmh: 'km/h', mph: 'mph', kn: 'kn' };
const KMH_PER_KNOT = 1.852;

// One literal t() per key so scripts/check-i18n.js sees every string.
function telemetryLabel(key, t) {
  switch (key) {
    case 'speed': return t('telemetry.speed', 'Speed');
    case 'heading': return t('telemetry.heading', 'Heading');
    case 'ignition': return t('telemetry.ignition', 'Ignition');
    case 'battery': return t('telemetry.battery', 'Battery');
    case 'voltage': return t('telemetry.voltage', 'Voltage');
    case 'fuel': return t('telemetry.fuel', 'Fuel');
    case 'sats': return t('telemetry.sats', 'Satellites');
    case 'odometer': return t('telemetry.odometer', 'Odometer');
    case 'temp': return t('telemetry.temp', 'Temp');
    case 'driver': return t('driverBehavior.driver', 'Driver');
    default: return key;
  }
}

const TELEMETRY_DEFS = [
  { key: 'speed', kind: 'number', unit: null },
  { key: 'heading', kind: 'number', unit: '°' },
  { key: 'ignition', kind: 'boolean', unit: null },
  { key: 'battery', kind: 'number', unit: '%' },
  { key: 'voltage', kind: 'number', unit: 'V' },
  { key: 'fuel', kind: 'number', unit: null },
  { key: 'sats', kind: 'number', unit: null },
  { key: 'odometer', kind: 'number', unit: 'km' },
  { key: 'temp', kind: 'number', unit: '°C' },
  { key: 'driver', kind: 'text', unit: null },
];

/** Catalog for the editor. `coordinates` is deliberately absent (it is a card, not a value). */
export function labelFieldCatalog({ device, t }) {
  const out = TELEMETRY_DEFS.map((d) => ({ ...d, label: telemetryLabel(d.key, t), group: 'telemetry' }));
  for (const def of CAN_SIGNALS) {
    out.push({ key: `can_${def.key}`, label: canLabel(def.key, t), unit: def.unit || null, kind: def.kind === 'boolean' ? 'boolean' : 'number', group: 'telemetry' });
  }
  for (const s of parseSensorConfig(device?.attributes?.sensorConfig)) {
    if (!s || typeof s.attribute !== 'string' || !s.attribute) continue;
    out.push({ key: `sensor:${s.attribute}`, label: s.name || s.attribute, unit: s.unit || null, kind: 'number', group: 'sensor' });
  }
  return out;
}

/** Label for ONE field key, without building the whole catalog (hot path: per vehicle). */
export function fieldLabel(key, { device, t }) {
  if (key.startsWith('can_')) return canLabel(key.slice(4), t);
  if (key.startsWith('sensor:')) {
    const attr = key.slice(7);
    const s = parseSensorConfig(device?.attributes?.sensorConfig).find((x) => x && x.attribute === attr);
    return (s && s.name) || attr;
  }
  if (key.startsWith('attr:')) return key.slice(5);
  return telemetryLabel(key, t);
}

/** Speed in knots, with a stationary fix pinned to 0. */
function speedKnots(ctx) {
  return isStationaryFix(ctx.position, ctx.thresholds) ? 0 : Number(ctx.position?.speed) || 0;
}

const dash = () => ({ value: null, text: DASH, unit: null });
const num = (v, decimals, unit) => (v == null || !Number.isFinite(Number(v)) ? dash() : { value: Number(v), text: Number(v).toFixed(decimals), unit });

/** One field's live value. `value` is typed for colour rules; `text` is for the label. */
export function readLabelField(key, ctx) {
  const { device, position, thresholds, speedUnit = 'kmh', fuelRow, t } = ctx;
  if (!position || isPositionStale(position)) return dash();
  const attrs = position.attributes || {};
  if (key === 'speed') {
    const v = Math.round(knotsToUnit(speedKnots(ctx), speedUnit));
    return { value: v, text: String(v), unit: SPEED_LABELS[speedUnit] || 'km/h' };
  }
  if (key === 'heading') return isStationaryFix(position, thresholds) ? dash() : num(position.course, 0, '°');
  if (key === 'ignition') {
    if (attrs.ignition === undefined) return dash();
    const on = Boolean(attrs.ignition);
    return { value: on, text: on ? t('vehicleList.on', 'On') : t('vehicleList.off', 'Off'), unit: null };
  }
  if (key === 'battery') return num(attrs.batteryLevel, 0, '%');
  if (key === 'voltage') return num(attrs.power ?? attrs.battery, 1, 'V');
  if (key === 'fuel') {
    const f = resolveFuelLevel({ overviewRow: fuelRow, device, position });
    return f.value == null ? dash() : { value: Number(f.value), text: Number(f.value).toFixed(1), unit: f.unit || null };
  }
  if (key === 'sats') return num(attrs.sat ?? attrs.satellites, 0, null);
  if (key === 'odometer') {
    const m = attrs.totalDistance ?? attrs.odometer;
    return m == null ? dash() : num(Number(m) / 1000, 0, 'km');
  }
  if (key === 'temp') return num(attrs.deviceTemp ?? attrs.temp, 1, '°C');
  if (key === 'driver') {
    const name = typeof attrs.driverName === 'string' ? attrs.driverName : (attrs.driverUniqueId != null ? String(attrs.driverUniqueId) : null);
    return name ? { value: name, text: name, unit: null } : dash();
  }
  if (key.startsWith('can_')) {
    const sig = key.slice(4);
    const def = CAN_SIGNALS.find((d) => d.key === sig);
    if (!def) return dash();
    const { value } = resolveCanSignals(device || {}, attrs)[sig] || {};
    if (value === null || value === undefined) return dash();
    if (def.kind === 'boolean') return { value: Boolean(value), text: value ? t('vehicleList.on', 'On') : t('vehicleList.off', 'Off'), unit: null };
    return { value: Number(value), text: Number(value).toFixed(def.decimals), unit: def.unit || null };
  }
  if (key.startsWith('sensor:')) {
    const attr = key.slice(7);
    const sensor = parseSensorConfig(device?.attributes?.sensorConfig).find((s) => s && s.attribute === attr);
    const raw = attrs[attr];
    if (raw == null || raw === '') return dash();
    const n = Number(raw);
    return { value: Number.isFinite(n) ? n : String(raw), text: formatSensorValue(raw), unit: sensor?.unit || null };
  }
  if (key.startsWith('attr:')) {
    const raw = attrs[key.slice(5)];
    if (raw == null || raw === '') return dash();
    const n = typeof raw === 'boolean' ? null : Number(raw);
    return { value: typeof raw === 'boolean' ? raw : (Number.isFinite(n) ? n : String(raw)), text: formatSensorValue(raw), unit: null };
  }
  return dash();
}

/** Convert a live value into the unit the rule's ranges were written in (km/h for speed). */
function valueForRule(key, field, ctx) {
  if (key === 'speed') {
    return speedKnots(ctx) * KMH_PER_KNOT;
  }
  return field.value;
}

export function labelColor(style, ctx) {
  if (style.colorMode === 'status') return STATUS_CONFIG[ctx.status]?.color || STATUS_CONFIG.offline.color;
  if (style.colorMode === 'variable' && style.variable) {
    const rule = style.variable;
    const field = readLabelField(rule.key, ctx);
    if (field.value === null) return rule.noDataColor;
    if (typeof field.value === 'boolean') return field.value ? (rule.onColor || rule.noDataColor) : (rule.offColor || rule.noDataColor);
    if (typeof field.value !== 'number') return rule.noDataColor;
    const v = valueForRule(rule.key, field, ctx);
    for (const r of rule.ranges) if (r.to === null || v <= r.to) return r.color;
    return rule.noDataColor;
  }
  return style.bg;
}

export function buildLabelProps(style, ctx) {
  const { device, t } = ctx;
  const name = typeof device?.name === 'string' && device.name.trim() ? device.name.trim() : DASH;
  const parts = [];
  if (style.shape !== 'none' && style.fields.length) {
    for (const key of style.fields) {
      const r = readLabelField(key, ctx);
      const value = `${r.text}${r.unit ? ` ${r.unit}` : ''}`;
      parts.push(style.showFieldLabels === false ? value : `${fieldLabel(key, { device, t })} ${value}`);
    }
  }
  // `singleLine`: name and values on one row (same separator); otherwise the
  // values form a second line under the name.
  const labelText = parts.length
    ? (style.singleLine ? [name, ...parts].join(' · ') : `${name}\n${parts.join(' · ')}`)
    : name;
  const props = {
    labelText,
    labelBg: labelColor(style, ctx),
    labelFg: style.fg,
    labelOpacity: style.bgOpacity,
    labelShape: style.shape,
    // Overlap now follows the org's crowding mode (spec 2026-10-30 §3.1): only
    // "Stack as cards" lets labels overlap, and it draws them as baked cards.
    labelOverlap: ctx.crowding === 'stack' ? 1 : 0,
    labelGlow: style.selectionGlow === false ? 0 : 1,
    labelCompact: style.shrinkWhenZoomedOut ? 1 : 0,
    labelOneLine: style.singleLine ? 1 : 0,
    labelTight: style.compact ? 1 : 0,
    labelTextSize: TEXT_SIZE_PX[style.textSize] || TEXT_SIZE_PX.normal,
    labelMinZoom: typeof style.minZoom === 'number' ? style.minZoom : 12,
  };
  if (ctx.crowding === 'stack') props.labelCard = registerCardSpec(cardSpec(props));
  return props;
}
